import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AccountView, PlanTier, SubscriptionView } from '@/types/api';
import { isTrialActive } from '@/lib/trial';

/**
 * AccountStore holds the tenant's `/accounts/me` payload. Persisted so a
 * refresh doesn't flash an unauthenticated layout — we can render the
 * header synchronously from cache while `<ProtectedRoute />` refetches in
 * the background.
 *
 * `isAdmin()` reads the `isAdmin` field stamped on the AccountView by
 * `AccountsService.getById` / `AuthService.findOrCreateAccount`. The
 * server-side `AdminGuard` independently enforces the same check on
 * every write endpoint, so this selector is UI-gating only — a client
 * that lies about the flag still can't POST to admin endpoints.
 */
export interface AccountState {
  account: AccountView | null;
  setAccount: (account: AccountView | null) => void;
  patchAccount: (partial: Partial<AccountView>) => void;
  /**
   * Replace just the subscription slice on the cached account. Used after
   * billing flows (Checkout success, portal-driven plan change) so the
   * Account page and header pill repaint immediately without waiting for
   * the next `/accounts/me` refetch.
   */
  setSubscription: (subscription: SubscriptionView) => void;
  isAdmin: () => boolean;
  /**
   * The premium-feature ACCESS gate: true when the cached account is either a
   * paying subscriber (`subscription.isPaid`) OR inside its app-level free
   * trial (`subscription.isInTrial`). Every paywalled surface gates on this.
   * Never gate on `planTier !== 'free'` directly — that would lock users out
   * the second a subscription enters `unpaid`, and would miss trial users
   * (who stay on the `free` tier).
   */
  isPaidSubscriber: () => boolean;
  /**
   * True while the account is inside its 3-day free trial. UI-only — drives
   * the trial countdown/upgrade nudge. Feature access already flows through
   * `isPaidSubscriber()`, which folds this in.
   */
  isInTrial: () => boolean;
  /**
   * Convenience accessor for the current plan tier, defaulting to
   * `'free'` when no account is loaded yet (so callers don't need a null
   * check before switching on tier).
   */
  planTier: () => PlanTier;
  reset: () => void;
}

export const useAccountStore = create<AccountState>()(
  persist(
    (set, get) => ({
      account: null,
      setAccount: (account) => set({ account }),
      patchAccount: (partial) => {
        const current = get().account;
        if (!current) return;
        set({ account: { ...current, ...partial } });
      },
      setSubscription: (subscription) => {
        const current = get().account;
        if (!current) return;
        set({ account: { ...current, subscription } });
      },
      isAdmin: () => get().account?.isAdmin === true,
      isPaidSubscriber: () => {
        const sub = get().account?.subscription;
        // Recompute the trial against the clock — never trust the persisted
        // `isInTrial` boolean, which can outlive the trial in localStorage.
        return sub?.isPaid === true || isTrialActive(sub);
      },
      isInTrial: () => isTrialActive(get().account?.subscription),
      planTier: () => get().account?.subscription?.planTier ?? 'free',
      reset: () => set({ account: null }),
    }),
    {
      name: 'account-store',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/** Build a display-ready name from first + last. Falls back to 'Reader'. */
export function formatAccountName(account: AccountView | null): string {
  if (!account) return 'Reader';
  const full = `${account.firstName} ${account.lastName}`.trim();
  return full.length > 0 ? full : 'Reader';
}
